/**
 * Payment Provider
 * 
 * Renders a single PaymentCheckoutModal instance at the app root level.
 * Subscribes to paymentStore and opens/closes the modal based on state.
 * 
 * This ensures:
 * - Only one payment modal instance exists (performance)
 * - Any component can trigger payment via usePayment()
 * - Balance is refreshed after successful payment
 * - Callbacks are properly invoked
 */

import React, { useCallback, useEffect, useSyncExternalStore } from 'react';
import { paymentStore } from '@/stores/paymentStore';
import { PaymentCheckoutModal } from '@/components/payment/PaymentCheckoutModal';
import { useBalance } from '@/contexts/BalanceContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileQuery } from '@/hooks/queries/useProfileQuery';
import { CheckCircle2 } from 'lucide-react';
import { trackPurchase } from '@/lib/pixel';

export interface PaymentSuccessData {
  isFirstPurchase?: boolean;
}

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { refreshBalance } = useBalance();
  const { user } = useAuth();
  const { data: profile } = useProfileQuery();

  // Subscribe to store state - only this component re-renders when payment state changes
  const state = useSyncExternalStore(
    paymentStore.subscribe,
    paymentStore.getState
  );

  /**
   * Handle successful payment:
   * 1. Track purchase conversion in Meta Pixel
   * 2. Refresh user's balance
   * 3. Show success animation and close modal
   */
  const handleSuccess = useCallback((data: PaymentSuccessData = {}) => {
    // Track purchase conversion in Meta Pixel
    if (user?.email && state.item?.price) {
      trackPurchase({
        email: user.email,
        username: profile?.username,
        value: state.item.price,
        currency: 'USD',
        isFirstPurchase: data.isFirstPurchase ?? false,
      });
    }
    
    refreshBalance();
    paymentStore.handleSuccess();
  }, [refreshBalance, user?.email, state.item?.price, profile?.username]);

  /**
   * Handle modal close (user cancelled)
   */
  const handleClose = useCallback(() => {
    paymentStore.closePayment();
  }, []);

  return (
    <>
      {children}

      {/* Success Animation Overlay - shows for 2 seconds after payment completes */}
      {state.showSuccess && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
          role="alert"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-4 animate-in zoom-in-50 duration-500">
            {/* Animated checkmark circle */}
            <div className="relative">
              {/* Outer ring animation */}
              <div className="absolute inset-0 rounded-full bg-green-500/20 animate-ping" />
              {/* Inner circle with checkmark */}
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-green-500/50">
                <CheckCircle2 className="w-14 h-14 text-white animate-in zoom-in duration-300 delay-200" strokeWidth={2.5} />
              </div>
            </div>
            {/* Success text */}
            <div className="text-center animate-in slide-in-from-bottom-4 duration-500 delay-300">
              <h2 className="text-2xl font-black text-white mb-1">Payment Successful!</h2>
              <p className="text-green-400 font-medium">Your coins are on the way</p>
            </div>
          </div>
        </div>
      )}

      {/* Payment checkout modal - handles both regular and coinflow-only modes
          Using a single component instance ensures checkout session is preserved
          when switching between modes */}
      {state.item && !state.showSuccess && (
        <PaymentCheckoutModal
          open={state.isOpen}
          onClose={handleClose}
          packageId={state.item.isScheduledOffer ? undefined : state.item.id}
          scheduledOfferId={state.item.isScheduledOffer ? state.item.id : undefined}
          packageTitle={state.item.title}
          amount={state.item.price}
          originalAmount={state.item.originalPrice}
          gcAmount={state.item.gcAmount}
          scAmount={state.item.scAmount}
          gcBonusAmount={state.item.gcBonusAmount}
          scBonusAmount={state.item.scBonusAmount}
          promoCode={state.item.promoCode}
          promoRewardType={state.item.promoRewardType}
          promoRewardValue={state.item.promoRewardValue}
          promoBonusGC={state.item.promoBonusGC}
          promoBonusSC={state.item.promoBonusSC}
          onSuccess={handleSuccess}
          coinflowOnly={state.item.coinflowOnly}
        />
      )}
    </>
  );
};
